import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FilingSource, MailChangedError, MailMetadata } from './filing-source';
import { DocumentField, FiledTarget, MAX_FILINGS } from './mail-upload.models';
import { MailUploadService } from './mail-upload.service';

/**
 * The folder a mail goes into: a folder's guid, null for the dossier root, or
 * a call that creates the folder first and answers its guid.
 */
export type FolderTarget = string | null | (() => Promise<string>);

/** Where to file a mail, and the document fields that go with it. */
export interface FilingRequest {
  dossierId: string;
  ordnerId: FolderTarget;
  /** Or made from the mail, for fields each mail fills in itself, once it is read. */
  felder: DocumentField[] | ((metadata: MailMetadata) => DocumentField[]);
}

/** Why a mail was not filed, ready for the notification. */
export interface FilingFailure {
  /** Translation key of the message. */
  errorKey: string;
  /** Translation key of the hint shown in place of the detail; set exactly for a final failure. */
  hintKey: string | null;
  /** Technical detail, meant to be readable without devtools. */
  detail: string;
}

/**
 * How filing one mail ended. Filed with a warning means the server took the
 * mail, but the mail itself could not be recorded or marked as filed. A final
 * failure fails the same way on every retry, so none is offered.
 */
export type FilingOutcome =
  | { status: 'filed' | 'filed-with-warning'; filed: FiledTarget }
  | ({ status: 'failed-retryable' | 'failed-final' } & FilingFailure);

/**
 * Errors that fail the same way on every retry, each with a hint in place of
 * the status line, which means nothing to the user.
 */
const FINAL_ERROR_HINTS: Record<string, string> = {
  'mailUpload.errorTooLarge': 'mailUpload.errorTooLargeHint',
  'mailUpload.errorTooManyFilings': 'mailUpload.errorTooManyFilingsHint'
};

/** The server's own explanation, when the error body carries one. */
function responseMessage(body: unknown): string | null {
  if (typeof body === 'string') {
    return body.trim() || null;
  }

  if (body !== null && typeof body === 'object') {
    for (const key of ['message', 'detail'] as const) {
      const value = (body as Record<string, unknown>)[key];

      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
  }

  return null;
}

/** Technical detail for the notification, meant to be readable without devtools. */
function describeError(error: unknown): string {
  if (error instanceof HttpErrorResponse) {
    // Status 0 covers a blocked preflight, an unreachable host, and a server
    // error whose response carried no CORS headers. The browser hides which.
    if (error.status === 0) {
      return 'No HTTP status: blocked by CORS, unreachable, or a server error without CORS headers';
    }

    // The body names the actual cause on the errors this API returns — which
    // mandatory field is missing on a 400, which license on a 403.
    const message = responseMessage(error.error);
    const status = `HTTP ${error.status} ${error.statusText}`;

    return message ? `${status}: ${message}` : status;
  }

  return error instanceof Error ? error.message : String(error);
}

/**
 * The outcome of a mail that failed with `errorKey`, final or not as the key
 * says. For a caller that fails a mail before the pipeline gets it.
 */
export function failedOutcome(errorKey: string, error: unknown): FilingOutcome {
  console.error('Filing the mail failed:', error);

  const hintKey = FINAL_ERROR_HINTS[errorKey] ?? null;

  return {
    status: hintKey ? 'failed-final' : 'failed-retryable',
    errorKey,
    hintKey,
    detail: describeError(error)
  };
}

/**
 * Files one mail: filing-limit check, EML read, upload, then the record and
 * the CMI mark on the mail. The same steps whichever {@link FilingSource} the
 * mail comes from.
 */
@Injectable({
  providedIn: 'root'
})
export class FilingPipelineService {
  private readonly _mailUploadService = inject(MailUploadService);

  /**
   * Every way filing can end is an outcome, so this rejects only when
   * `onFiled` itself throws.
   *
   * `onFiled` runs as soon as the server has the mail, before the record and
   * the mark are written, so a caller can confirm without waiting for them.
   * The record is already in memory by then (see FilingsService).
   */
  public async file(
    source: FilingSource,
    request: FilingRequest,
    onFiled?: (filed: FiledTarget) => void
  ): Promise<FilingOutcome> {
    // The record on the mail holds at most MAX_FILINGS filings, so the limit is
    // checked before anything reaches the server. A record that cannot be read
    // stops the filing too: filing blind could pass the limit unnoticed.
    let filings: FiledTarget[];

    try {
      filings = await source.getFilings();
    } catch (error) {
      return failedOutcome('mailUpload.errorReadFilings', error);
    }

    if (filings.length >= MAX_FILINGS) {
      return failedOutcome(
        'mailUpload.errorTooManyFilings',
        new Error(`The mail has already been filed ${MAX_FILINGS} times`)
      );
    }

    let metadata: MailMetadata;
    let mailEmlBase64: string;

    try {
      metadata = await source.getMetadata();
      mailEmlBase64 = await source.getEmlBase64();
    } catch (error) {
      return failedOutcome(
        error instanceof MailChangedError ? 'mailUpload.errorMailChanged' : 'mailUpload.errorRead',
        error
      );
    }

    const { subject, sentDateTime, ...addresses } = metadata;
    const felder = typeof request.felder === 'function' ? request.felder(metadata) : request.felder;

    // A new folder is created only now, once the mail is read and about to go:
    // a mail that cannot be filed leaves no empty folder behind.
    let ordnerId: string | null;

    try {
      ordnerId =
        typeof request.ordnerId === 'function' ? await request.ordnerId() : request.ordnerId;
    } catch (error) {
      return failedOutcome('mailUpload.errorCreateFolder', error);
    }

    try {
      await this._mailUploadService.fileMail({
        dossierId: request.dossierId,
        ordnerId,
        betreff: subject,
        ...addresses,
        mailEmlBase64,
        felder
      });
    } catch (error) {
      const tooLarge = error instanceof HttpErrorResponse && error.status === 413;
      return failedOutcome(tooLarge ? 'mailUpload.errorTooLarge' : 'mailUpload.errorUpload', error);
    }

    const filed: FiledTarget = {
      dossierGuid: request.dossierId,
      folderGuid: ordnerId,
      filedAt: new Date().toISOString()
    };

    // Started before onFiled, so the record is in memory when the caller confirms.
    const recorded = this._recordFiling(source, filed);
    onFiled?.(filed);

    // Neither write can fail a filed mail: it is on the server either way. The
    // mark goes on even when the record could not be written.
    const isRecorded = await recorded;
    const isMarked = await this._markFiled(source);

    return { status: isRecorded && isMarked ? 'filed' : 'filed-with-warning', filed };
  }

  /** Notes the target on the mail itself, so reopening it shows where it went. */
  private async _recordFiling(source: FilingSource, filed: FiledTarget): Promise<boolean> {
    try {
      await source.recordFiling(filed);
      return true;
    } catch (error) {
      console.error('Recording the filing on the mail failed:', error);
      return false;
    }
  }

  /**
   * The CMI mark in Outlook, put on after the confirmation is already up: it is
   * a label on a mail that is filed either way, so a mailbox that refuses it
   * must not turn a filed mail into a failed one.
   */
  private async _markFiled(source: FilingSource): Promise<boolean> {
    try {
      await source.markFiled();
      return true;
    } catch (error) {
      console.error('Marking the mail as filed in Outlook failed:', error);
      return false;
    }
  }
}
